import { useEffect, useState } from "react";
import { useT } from "../i18n";
import { useVault } from "../vault/store";
import { MIN_MASTER_LEN } from "../vault/policy";
import { getAppSettings, setAppSettings, type AppSettings } from "../desktop/appSettings";
import { getRdpUseNative, setRdpUseNative, getRestoreTabs, setRestoreTabs, type SecurityPrefs } from "../vault/prefs";
import { OfflineSection } from "./OfflineSection";
import { ConnectExtension } from "./ConnectExtension";
import { isChromium } from "./extension-bridge";
import { Icons } from "./icons";

// Unified app settings (UX phase 5). User/device-level preferences in a tabbed modal: General
// (language), Security (idle-lock + clipboard-clear, enforced app-side §20), a desktop-only
// Offline section (§15), and About. Org-level settings (offline default, branding) live in the
// per-vault "Vault defaults" dialog, not here.
type Tab = "general" | "security" | "browser" | "desktop" | "offline" | "about";

export function Settings({
  security,
  onSaveSecurity,
  lang,
  onSetLang,
  cryptoVer,
  desktop,
  appVersion,
  login,
  onClose,
}: {
  security: SecurityPrefs;
  onSaveSecurity: (p: SecurityPrefs) => void;
  lang: "cs" | "en";
  onSetLang: (l: "cs" | "en") => void;
  cryptoVer: number;
  desktop: boolean;
  appVersion?: string;
  login: string;
  onClose: () => void;
}) {
  const t = useT();
  const [tab, setTab] = useState<Tab>("general");
  // Security is staged in a draft and committed on Save; language applies live.
  const [idleLockMin, setIdleLockMin] = useState(String(security.idleLockMin));
  const [clipboardClearSec, setClipboardClearSec] = useState(String(security.clipboardClearSec));
  const [hideLockGraceSec, setHideLockGraceSec] = useState(String(security.hideLockGraceSec));
  // RDP renderer default applies live (localStorage); a per-item "RDP client" field overrides it.
  const [rdpNative, setRdpNative] = useState(() => getRdpUseNative());

  function save() {
    onSaveSecurity({
      idleLockMin: parseInt(idleLockMin, 10),
      clipboardClearSec: parseInt(clipboardClearSec, 10),
      hideLockGraceSec: parseInt(hideLockGraceSec, 10),
    });
    onClose();
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: "general", label: t("General") },
    { id: "security", label: t("Security") },
    // The extension pairs from the web build inside Chrome/Edge — not from the desktop app.
    // Shown on any Chromium browser (even before install) so the setup guide is reachable.
    ...(!desktop && isChromium() ? [{ id: "browser" as Tab, label: t("Browser") }] : []),
    ...(desktop ? [{ id: "desktop" as Tab, label: t("Desktop") }] : []),
    ...(desktop ? [{ id: "offline" as Tab, label: t("Offline") }] : []),
    { id: "about", label: t("About") },
  ];
  const input = "w-24 rounded border border-border bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60" onClick={onClose}>
      <div className="flex max-h-[80vh] w-[520px] flex-col rounded-md border border-border bg-panel shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-border px-5 py-3 font-semibold"><Icons.settings className="h-4 w-4" /> {t("Settings")}</div>
        <div className="flex items-center gap-1 border-b border-border px-3 pt-2 text-sm">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              className={`rounded-t border-b-2 px-3 py-1.5 ${tab === tb.id ? "border-accent text-fg" : "border-transparent text-muted hover:text-fg"}`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-5 py-5 text-sm">
          {tab === "general" && (
            <div className="flex flex-col gap-4">
              <Field label={t("Language")} hint={t("Interface language.")}>
                <select value={lang} onChange={(e) => onSetLang(e.target.value as "cs" | "en")} className={`${input} w-32`}>
                  <option value="en">English</option>
                  <option value="cs">Čeština</option>
                </select>
              </Field>
              {desktop && (
                <Field
                  label={t("Use native RDP client")}
                  hint={t("Open RDP sessions in the native OS client instead of the in-app viewer. A per-item setting can override this.")}
                >
                  <input
                    type="checkbox"
                    checked={rdpNative}
                    onChange={(e) => {
                      setRdpNative(e.target.checked);
                      setRdpUseNative(e.target.checked);
                    }}
                    className="h-4 w-4"
                  />
                </Field>
              )}
            </div>
          )}

          {tab === "security" && (
            <div className="flex flex-col gap-4">
              <Field label={t("Auto-lock after")} hint={t("Lock the vault after this many minutes of inactivity (1–240).")}>
                <span className="flex items-center gap-1.5">
                  <input type="number" min={1} max={240} value={idleLockMin} onChange={(e) => setIdleLockMin(e.target.value)} className={input} />
                  <span className="text-muted">{t("min")}</span>
                </span>
              </Field>
              <Field
                label={t("Lock hidden tab after")}
                hint={t("Grace period before switching away from this tab locks the vault (0–300 s; 0 locks immediately).")}
              >
                <span className="flex items-center gap-1.5">
                  <input type="number" min={0} max={300} value={hideLockGraceSec} onChange={(e) => setHideLockGraceSec(e.target.value)} className={input} />
                  <span className="text-muted">s</span>
                </span>
              </Field>
              <Field label={t("Clear clipboard after")} hint={t("Wipe copied secrets from the clipboard after this many seconds (5–600).")}>
                <span className="flex items-center gap-1.5">
                  <input type="number" min={5} max={600} value={clipboardClearSec} onChange={(e) => setClipboardClearSec(e.target.value)} className={input} />
                  <span className="text-muted">s</span>
                </span>
              </Field>
              <ChangeMasterForm />
            </div>
          )}

          {tab === "browser" && !desktop && <ConnectExtension login={login} />}

          {tab === "desktop" && desktop && <DesktopTab />}

          {tab === "offline" && desktop && <OfflineSection />}

          {tab === "about" && (
            <dl className="flex flex-col gap-2 text-sm">
              <About k={t("Application")} v="Vault & Remote Access Manager" />
              {desktop && appVersion && <About k={t("App version")} v={`v${appVersion}`} />}
              <About k={t("Platform")} v={desktop ? t("Desktop") : t("Web")} />
              <About k={t("Crypto format")} v={`v${cryptoVer}`} />
              <p className="mt-2 text-xs text-muted">{t("End-to-end encrypted. Secrets are decrypted only in your browser (§4).")}</p>
            </dl>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <button onClick={onClose} className="rounded border border-border px-3 py-1.5 text-sm hover:border-accent">{t("Cancel")}</button>
          <button onClick={save} className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent2">{t("Save")}</button>
        </div>
      </div>
    </div>
  );
}

function ChangeMasterForm() {
  const t = useT();
  const changeMaster = useVault((s) => s.changeMaster);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const tooShort = next.length > 0 && next.length < MIN_MASTER_LEN;
  const mismatch = confirm.length > 0 && next !== confirm;
  const sameAsCur = next.length > 0 && next === cur;
  const canSubmit = !!cur && next.length >= MIN_MASTER_LEN && next === confirm && next !== cur && !busy;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    setDone(false);
    try {
      await changeMaster(cur, next);
      setDone(true);
      setCur("");
      setNext("");
      setConfirm("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not change master password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="mt-1 flex flex-col gap-2 rounded border border-border p-3">
      <div className="font-medium">{t("Change master password")}</div>
      <p className="text-xs text-muted">
        {t("Re-encrypts your vault keys under a new password. It is never sent to the server and cannot be recovered.")}
      </p>
      <PwInput label={t("Current password")} value={cur} onChange={setCur} />
      <PwInput label={`${t("New password")} (${t("min")} ${MIN_MASTER_LEN})`} value={next} onChange={setNext} />
      <PwInput label={t("Confirm new password")} value={confirm} onChange={setConfirm} />
      {tooShort && <Hint danger>{`${t("At least")} ${MIN_MASTER_LEN} ${t("characters.")}`}</Hint>}
      {mismatch && <Hint danger>{t("Passwords do not match.")}</Hint>}
      {sameAsCur && <Hint danger>{t("New password must differ from the current one.")}</Hint>}
      {err && <Hint danger>{err}</Hint>}
      {done && <Hint>{t("Master password changed.")}</Hint>}
      <button
        type="submit"
        disabled={!canSubmit}
        className="mt-1 self-start rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent2 disabled:opacity-60"
      >
        {busy ? "…" : t("Change password")}
      </button>
    </form>
  );
}

function DesktopTab() {
  const t = useT();
  const [app, setApp] = useState<AppSettings>({});
  const [restoreTabs, setRestoreTabsState] = useState(() => getRestoreTabs());
  useEffect(() => {
    void getAppSettings().then(setApp);
  }, []);
  const patch = (p: Partial<AppSettings>) => {
    setApp((cur) => ({ ...cur, ...p }));
    void setAppSettings(p);
  };
  return (
    <div className="flex flex-col gap-4">
      <Field label={t("Start maximized")} hint={t("Open the window maximized on every launch.")}>
        <input type="checkbox" checked={!!app.startMaximized} onChange={(e) => patch({ startMaximized: e.target.checked })} className="h-4 w-4" />
      </Field>
      <Field label={t("Minimize to tray on close")} hint={t("Closing the window hides it to the system tray and keeps the app running.")}>
        <input type="checkbox" checked={!!app.closeToTray} onChange={(e) => patch({ closeToTray: e.target.checked })} className="h-4 w-4" />
      </Field>
      <Field label={t("Restore open tabs")} hint={t("Reopen the tabs you had open, after the vault unlocks on next launch.")}>
        <input
          type="checkbox"
          checked={restoreTabs}
          onChange={(e) => {
            setRestoreTabsState(e.target.checked);
            setRestoreTabs(e.target.checked);
          }}
          className="h-4 w-4"
        />
      </Field>
      <p className="text-xs text-muted">{t("Window size & position are remembered automatically.")}</p>
    </div>
  );
}

function PwInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-muted">{label}</span>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-border bg-panel2 px-2 py-1.5 text-sm outline-none focus:border-accent"
      />
    </label>
  );
}

function Hint({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div className={`text-xs ${danger ? "text-danger" : "text-accent"}`}>{children}</div>;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex items-start justify-between gap-4">
      <span className="flex flex-col">
        <span className="font-medium">{label}</span>
        {hint && <span className="text-xs text-muted">{hint}</span>}
      </span>
      <span className="shrink-0">{children}</span>
    </label>
  );
}

function About({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-muted">{k}</dt>
      <dd className="font-medium">{v}</dd>
    </div>
  );
}
